import React from 'react'
import { Prompt } from 'react-router-dom'
import { Skeleton, Button, Form, Input, Select, Row, Col, Tabs, Result, Popover, Space, Modal, Divider, message } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import * as deepObject from 'deep-object-diff'
import EmailTemplateStruct from './EmailTemplateStruct'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import IEmailTemplate from '../../../interfaces/IEmailTemplate'
import ITranslationEmailTemplate from '../../../interfaces/ITranslationEmailTemplate'
import ILanguage from '../../../interfaces/ILanguage'
import ISmartResponseStruct from '../../../interfaces/ISmartResponseStruct'
import { SmartResponseConstants } from '../../../helpers/enums/SmartResponse'
import { SearchRequest, EmailTemplatePreview, EmailTemplateUpdate, EmailTemplateTranslationSave, EmailTemplateTranslationDelete } from '../../../services/api/interfaces/ApiRequest'

type EmailTemplateProps = BaseFormComponentProps & {
  templateUuid: string,
  data?: IEmailTemplate,
  error: any | null,

  languages: ILanguage[] | null,
  preview: any | null,

  loading: boolean,
  contentLoading: boolean,
  history: any,

  loadLanguages: (args: SearchRequest) => void,
  loadEmailTemplate: (args: SearchRequest) => void,
  loadPreview: (args: EmailTemplatePreview) => void,
  
  dispatchSave: (args: EmailTemplateUpdate) => Promise<any>
  dispatchSaveTranslation: (args: EmailTemplateTranslationSave) => Promise<any>,
  dispatchDeleteTranslation: (args: EmailTemplateTranslationDelete) => Promise<any>,
}

export default class EmailTemplateForm extends FormComponent<EmailTemplateProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    saving: false,
    loaded: false,
    addTranslationVisible: false,
    translations: [] as ITranslationEmailTemplate[],
    selectedAddLanguage: undefined,
    changedForms: [] as string[],
    currentTab: 'general'
  }

  componentDidMount() {
    const { loadLanguages } = this.props

    // Check permissions
    this.validateAccessPermissions()

    // Load langues
    loadLanguages({
      search: {
        deleted: false
      },
      options: {
        _force_ids: true
      }
    })
  }

  componentDidUpdate(prevProps: EmailTemplateProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded?
    if (!this.state.loaded) {
      // Load email template
      if (this.props.templateUuid && !this.props.contentLoading && !this.props.loading && !wasLoading) {
        this.props.loadEmailTemplate({
          search: {
            uuid: this.props.templateUuid,
            deleted: false,
            email_template: {
              with: true
            },
            language: {
              with: true
            },
            translation: {
              with: {
                deleted: false,
                language: {
                  with: true
                }
              }
            }
          },
          options: {
            _force_ids: true
          }
        })
      } else {
        // If our entity was loaded or if we are creating a new one, that's means that we are loaded :P
        if (wasLoading || !this.props.templateUuid) {
          newState.loaded = true
        } 
  
        if (wasLoading && this.props.data) {
          newState.translations = this.props.data.translation || []
        }
      }

    } else {
      // We are saving
      if (this.state.saving && wasLoading) {
        // Add the new template uuid to url
        if (!this.props.templateUuid && this.props.data) {
          this.props.history.replace(`/organization/smart-response/${this.props.data.uuid}`)
        }

        if (!this.props.error) {
          message.success('Template saved successfully!')
          
          // Update translations
          if (this.props.data) {
            newState.translations = this.props.data.translation || []
          }
        } else {
          message.error('Failed to save user. Error: ' + this.props.error)
        }

        newState.saving = false
      }
    }

    // Update if changed
    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  handleTabChange = (activeTab: string) => {
    this.setState({
      currentTab: activeTab
    })
  }

  handleTabFormChange = (uuid: string) => {
    const { changedForms } = this.state
    
    if (!changedForms.includes(uuid)) {
      changedForms.push(uuid)
      this.setState({
        changedForms
      })
    }
  }

  handleAddTranslation = (copyBase: boolean = false) => {
    const { data, languages } = this.props
    const { translations, selectedAddLanguage } = this.state
    const lang = languages?.find(lang => lang.uuid === selectedAddLanguage)

    if (lang && data?.id) {
      translations.push({
        uuid: null,
        parent_id: data.id,
        language: lang,
        json: copyBase ? data.json : {
          model: {
            ...data?.json?.model
          },
          values: {}
        }
      })
    }

    this.setState({
      translations,
      currentTab: `language-${lang?.abbreviation || 'base'}`,
      selectedAddLanguage: undefined
    })
  }

  handleRemoveTranslation = (translation: ITranslationEmailTemplate) => {
    const { dispatchDeleteTranslation } = this.props
    const { translations } = this.state

    Modal.confirm({
      title: `Remove ${translation.language?.name} translation`,
      content: `Are you sure about removing ${translation.language?.name} translation?`,
      icon: <ExclamationCircleOutlined />,
      cancelText: 'No',
      okText: 'Yes',
      onOk: () => {
        if (translation.uuid) {
          dispatchDeleteTranslation({
            email_template_translation_uuid: translation.uuid
          })
        }

        this.setState({
          saving: !!translation.uuid,
          currentTab: 'general',
          translations: translations.filter(p => p !== translation)
        })
      }
    })
  }

  handleSaveTranslation = (uuid: string | null, data: ISmartResponseStruct, language_id?: number, parent_id?: number) => {
    const { templateUuid, dispatchSave, dispatchSaveTranslation } = this.props
    const { changedForms } = this.state

    // We are updating the base translation? (English)
    if (uuid === templateUuid) {
      const payload: EmailTemplateUpdate = {
        email_template: {
          uuid: templateUuid,
          json: data
        }
      }

      dispatchSave(payload)
    } else {
      // We are updating other translation
      const payload: EmailTemplateTranslationSave = {
        email_template_translation: {
          uuid: uuid,
          language_id: language_id as number,
          parent_id: parent_id as number,
          json: data
        }
      }

      dispatchSaveTranslation(payload)
    }

    
    this.setState({
      saving: true,
      changedForms: changedForms.filter(changedUuid => changedUuid !== (uuid || templateUuid))
    })
  }

  handleSaveGeneral = (values: any) => {
    const { templateUuid, dispatchSave } = this.props

    const payload: EmailTemplateUpdate = {
      email_template: {
        ...values.email_template,
        icon: values.icon,
        uuid: templateUuid
      }
    }

    dispatchSave(payload)
    this.setState({
      saving: true
    })
  }

  renderIconSelect = () => {
    return (
      <Select
        allowClear={true}
      >
        {
          SmartResponseConstants.ICONS.map((icon, index) => {
            return (
              <Select.Option key={`icon-${index}`} value={icon.key}>
                {icon.name} ({icon.key})
              </Select.Option>
            )
          })
        }
      </Select>
    )
  }

  renderTranslationTabs = () => {
    const { loading } = this.props
    const { translations, changedForms } = this.state

    return translations.map(translation => (
      <Tabs.TabPane
        key={`language-${translation.language?.abbreviation}`}
        tab={`${translation.language?.name} ${changedForms.includes(translation.uuid || '') ? '*' : ''}`}
      >
        {this.renderLanguageTabContent(translation)}

        <Divider />

        <Button type="default" size="large" loading={loading} onClick={() => this.handleRemoveTranslation(translation)} block>
          Remove
        </Button>   
      </Tabs.TabPane>
    ))
  }

  renderLanguageTabContent = (translation: ITranslationEmailTemplate) => {
    const { templateUuid, loading, preview, loadPreview } = this.props
    const data = typeof translation.json === 'string' ? JSON.parse(translation.json as any) : translation.json

    return (
      <EmailTemplateStruct
        templateUuid={templateUuid}
        uuid={translation.uuid}
        data={data}
        parent_id={translation.parent_id}
        language_id={translation.language?.id}
        preview={preview}
        loading={loading}
        loadPreview={loadPreview}
        onSave={this.handleSaveTranslation}
        onChange={this.handleTabFormChange}
      />
    )
  }

  renderGeneral = () => {
    const { loading, data, templateUuid } = this.props

    let values:any = {}

    if (templateUuid) {
      values = {
        ...data || {}
      }
    }

    return (
      <Form
        name="organization_email_template"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSaveGeneral}
      >
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Key"
              name={['email_template', 'key']}
              rules={[
                {
                  required: true,
                  message: 'Missing a key'
                }
              ]}
            >
              <Input readOnly disabled />
            </Form.Item>

            <Form.Item
              label="Name"
              name={['email_template', 'name']}
              rules={[
                {
                  required: true,
                  message: 'Missing a name'
                }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Icon"
              name="icon"
            >
              {this.renderIconSelect()}
            </Form.Item>

            <Form.Item style={{marginTop: 32}}>
              <Button type="primary" htmlType="submit" size="large" loading={loading} ref={(refs) => this.actionRefs.save = refs} block>
                Save
              </Button>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Description"
              name={['email_template', 'description']}
            >
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>
        </Row> 
      </Form>
    )
  }

  renderAddTranslation = () => {
    const { languages, data } = this.props
    const { translations, addTranslationVisible, selectedAddLanguage } = this.state

    const selectedLangs = translations.map(p => p.language?.abbreviation)
    if (data?.language) {
      selectedLangs.push(data.language.abbreviation)
    }

    const content = (
      <Space direction="vertical">
        <Select
          placeholder="Language"
          value={selectedAddLanguage}
          onChange={(value) => this.setState({ selectedAddLanguage: value })}
        >
          {languages?.filter(lang => !selectedLangs.includes(lang.abbreviation)).map((lang, index) => (
            <Select.Option key={`select-language-${index}`} value={lang.uuid}>{lang.name}</Select.Option>
          ))}
        </Select>

        <Button type="primary" disabled={!selectedAddLanguage} block onClick={() => {
          this.setState({
            addTranslationVisible: false
          }, () => {
            Modal.confirm({
              title: 'Do you want to copy the base (English) translation?',
              content: 'Copying the base translation, will make all models and values be filled. You still will be able to edit all fields as you wish.',
              icon: <ExclamationCircleOutlined />,
              cancelText: 'No',
              okText: 'Yes (Recommended)',
              onOk: () => this.handleAddTranslation(true),
              onCancel: () => this.handleAddTranslation()
            })
          })
        }}>
          Add
        </Button>
      </Space>
    )

    return (
      <Popover
        content={content}
        title="New Translation"
        trigger="click"
        visible={addTranslationVisible}
        onVisibleChange={(visible) => {
          this.setState({
            addTranslationVisible: visible,
            selectedAddLanguage: undefined
          })
        }}
        style={{minWidth: 300}}
      >
        <Button
          type="primary"
          shape="round"
          disabled={addTranslationVisible}
          onClick={() => {
            this.setState({
              addTranslationVisible: true
            })
          }}
        >
          Add
        </Button>
      </Popover>
    )
  }

  render() {
    const { templateUuid, data, preview, loading, loadPreview } = this.props
    const { loaded, currentTab, translations, changedForms } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    if (!data) {
      return <Result
        status="404"
        title="Not found"
        subTitle="Sorry, the page you visited does not exist."
      />
    }

    const unsavedChanges = translations.filter(p => !p.uuid).length > 0 || changedForms.length > 0

    return (
      <>
        <Tabs activeKey={currentTab} onChange={this.handleTabChange} tabBarExtraContent={this.renderAddTranslation()}>
          <Tabs.TabPane key="general" tab="General">
            {this.renderGeneral()}
          </Tabs.TabPane>

          <Tabs.TabPane
            key={`language-base`}
            tab={`${data.language?.name} ${changedForms.includes(templateUuid || '') ? '*' : ''}`}
          >
            <EmailTemplateStruct
              templateUuid={templateUuid}
              uuid={templateUuid as any}
              data={data.json as any}
              preview={preview}
              loading={loading}
              loadPreview={loadPreview}
              onChange={this.handleTabFormChange}
              onSave={this.handleSaveTranslation}
            />
          </Tabs.TabPane>

          {
            // Render language tabs
            this.renderTranslationTabs()
          }

          <Prompt
            when={unsavedChanges}
            message="You have unsaved changes, do you want to leave anyway?"
          />
        </Tabs>
      </>
    )
  }
}